<script lang="ts" setup>
import { ref } from 'vue'

import { guessGuidePlatform, type GuidePlatform } from '@/utils/file-pick-ladder'

// Plain steps for copying an event folder off a USB drive into the phone's own storage, so the
// browser can pick its files. Text only: not verified on real devices yet (RF-110).

const props = defineProps<{ platform?: GuidePlatform }>()

const tab = ref<GuidePlatform>(
  props.platform ?? guessGuidePlatform(navigator.userAgent, navigator.maxTouchPoints)
)

const ANDROID_STEPS = [
  'Plug the USB drive into your phone with a USB-C adapter (OTG).',
  'Open the Files app (Files by Google). Under Storage devices, tap the USB drive.',
  'Open TeslaCam, then SavedClips or SentryClips.',
  'Long-press the event folder (it is named like 2026-03-14_08-30-00), then tap Copy to Downloads.',
  'Come back here and tap "Choose event files". Open the folder you copied, then tap Select all.'
]

const IOS_STEPS = [
  'Plug the USB drive in. iPhones with a Lightning port need a camera adapter, and may need power too.',
  'Open the Files app, tap Browse, then tap your drive.',
  'Open TeslaCam, then SavedClips or SentryClips.',
  'Long-press the event folder (it is named like 2026-03-14_08-30-00), then tap Copy.',
  'Go to On My iPhone (or On My iPad), long-press an empty spot and tap Paste. An event can be hundreds of MB to a few GB, so check you have the space.',
  'Come back here and tap "Choose event files". Open the folder you pasted, tap Select, then Select All.'
]
</script>

<template>
  <div data-testid="copy-to-files-guide">
    <p class="text-body2 text-blue-grey-8 q-mb-sm">
      Copy one event to your phone first, then pick its files here. Nothing is deleted from the
      drive.
    </p>

    <q-tabs
      v-model="tab"
      dense
      align="left"
      active-color="blue-8"
      indicator-color="blue-8"
      class="text-blue-grey-7"
    >
      <q-tab name="android" label="Android" no-caps data-testid="guide-tab-android" />
      <q-tab name="ios" label="iPhone & iPad" no-caps data-testid="guide-tab-ios" />
    </q-tabs>

    <q-tab-panels v-model="tab" animated>
      <q-tab-panel name="android" class="q-px-none">
        <ol class="guide-steps text-body2 text-blue-grey-9">
          <li v-for="step in ANDROID_STEPS" :key="step">{{ step }}</li>
        </ol>
      </q-tab-panel>
      <q-tab-panel name="ios" class="q-px-none">
        <ol class="guide-steps text-body2 text-blue-grey-9">
          <li v-for="step in IOS_STEPS" :key="step">{{ step }}</li>
        </ol>
      </q-tab-panel>
    </q-tab-panels>

    <q-banner dense rounded class="bg-orange-1 text-blue-grey-9 text-body2">
      <template #avatar><q-icon name="mdi-alert-circle-outline" color="orange-9" /></template>
      <div>
        The drive must be exFAT or FAT32. iPhones and iPads cannot read NTFS, and some older Android
        phones cannot read exFAT, so use a computer for those.
      </div>
      <div class="q-mt-xs">
        Turn off USB encryption in your Tesla first. We cannot read encrypted footage.
      </div>
    </q-banner>
  </div>
</template>

<style scoped>
.guide-steps {
  margin: 0;
  padding-left: 1.4em;
}

.guide-steps li + li {
  margin-top: 0.5em;
}
</style>
